import React, { useState, ChangeEvent } from 'react'
import {
  Box,
  Typography,
  Switch,
  Grid,
  Button,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Card,
  CardContent as MuiCardContent,
} from '@mui/material'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import styled from 'styled-components';
import {
  PriceDataInterface,
  productHostedMeeting,
  productOptions,
} from '../data/subscription';
import UserQuantityDialog from './UserQuantityDialog';

interface PricingSectionProps {
  onSelectPrices?: (prices: Array<string>, quantity: number) => void;
  defaultInterval: 'month' | 'year';
  defaultQuantity: number;
  skipQuantity?: boolean;
  canChangeInterval: boolean;
  enabledProducts: string[];
  shouldTrial: boolean;
}

const Section = styled.div`
  padding: 50px 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const StyledCardContent = styled(MuiCardContent)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px;
`;

const PricingCard = styled(Card)`
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease-in-out;
  border: 1px solid #1976d2;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;


const PricingSection: React.FC<PricingSectionProps> = ({
  onSelectPrices,
  enabledProducts,
  shouldTrial,
  defaultInterval = 'month',
  canChangeInterval = true,
  defaultQuantity = 1,
  skipQuantity = false,
}) => {
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(defaultInterval);
  const [meetingAddonSelected, setMeetingAddonSelected] = useState(false);
  const [priceSelected, setPriceSelected] = useState<null | PriceDataInterface[]>(null);

  const unitPrice: undefined | number = priceSelected ? priceSelected.reduce(
    (accum, val) => (accum + val.amount),
    0
  ) : undefined;

  const toggleBillingInterval = (e: ChangeEvent<HTMLInputElement>) => {
    setBillingInterval(e.target.checked ? 'month' : 'year')
  }

  const toggleMeetingAddon = (e: ChangeEvent<HTMLInputElement>) => {
    setMeetingAddonSelected(e.target.checked)
  }

  const getPrices = (product: string) => {
    const resultList = [];
    const key = productOptions[product]?.prices?.[billingInterval];
    if (key) {
      resultList.push(key);
    }
    if (meetingAddonSelected) {
      resultList.push(productHostedMeeting.prices[billingInterval]);
    }
    return resultList;
  }

  const handleQuantitySelected = (quantity: number) => {
    if (priceSelected === null) {
      return;
    }
    onSelectPrices?.(priceSelected.map(p => p.priceKey), quantity);
    setPriceSelected(null)
  }

  const handlePriceSelected = (
    prices: PriceDataInterface[],
    quantity?: number,
  ) => {
    const price_keys = prices.map(p => p.priceKey);
    if (skipQuantity) {
      onSelectPrices?.(price_keys, 1);
    } else if (typeof quantity === 'number') {
      onSelectPrices?.(price_keys, quantity);
    } else {
      setPriceSelected(prices)
    }
  }

  return (
    <Section id="pricing">
      {canChangeInterval && (
        <>
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Typography variant='body1'>Billed Yearly</Typography>
            <Switch
              checked={billingInterval == 'month'}
              onChange={toggleBillingInterval}
              color='primary'
            />
            <Typography variant='body1'>Billed Monthly</Typography>
          </Box>
          <Typography textAlign="center">(Save 20% on yearly Subscription)</Typography>
        </>
      )}

      <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
        {/* Free Plan */}
        {enabledProducts.includes('free') && (
          <Grid item xs={12} md={4}>
            <PricingCard>
              <StyledCardContent>
                <Box>
                  <Typography variant="h5" component="div" fontWeight={600} gutterBottom>
                    Free Plan
                  </Typography>
                  <Typography variant="h3" component="div" gutterBottom>
                    <Typography component="span">AUD</Typography>$0
                    <Typography component="span" variant="subtitle1">/month</Typography>
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" textAlign="center" gutterBottom>
                    COLLECT
                  </Typography>
                  <List>
                    {productOptions.free.description.map((feature, index) => (
                      <ListItem key={index} alignItems="flex-start" disablePadding>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <CheckCircleIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ fontSize: "15px" }} sx={{ marginTop: "8px" }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => handlePriceSelected(
                      getPrices('free'),
                      1
                    )}
                  >
                    Setup Now
                  </Button>
                </Box>
              </StyledCardContent>
            </PricingCard>
          </Grid>
        )}

        {/* Individual Plan */}
        {enabledProducts.includes('prod_tl_individual-2025-wGCk4x2O6p3j') && (
          <Grid item xs={12} md={4}>
            <PricingCard>
              <StyledCardContent>
                <Box>
                  <Box>
                    <Typography variant="h5" component="div" fontWeight={600} gutterBottom>
                      Individual Plan
                    </Typography>
                    <Typography variant="h3" component="div" gutterBottom>
                      <Typography component="span">AUD</Typography>${productOptions['prod_tl_individual-2025-wGCk4x2O6p3j'].prices?.[billingInterval].amount ?? '?'}
                      <Typography component="span" variant="subtitle1">/{billingInterval}</Typography>
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" textAlign="center" gutterBottom>
                      COLLECT & MEET
                    </Typography>
                    <List>
                      <ListItem>Everything in "Free Plan" +</ListItem>
                      {productOptions['prod_tl_individual-2025-wGCk4x2O6p3j'].description.map((feature, index) => (
                        <ListItem key={index} alignItems="flex-start" disablePadding>
                          <ListItemIcon sx={{ minWidth: '40px' }}>
                            <CheckCircleIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} primaryTypographyProps={{ fontSize: "15px" }} sx={{ marginTop: "8px" }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
                <Box>
                  <Box>
                    {/* <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={<Checkbox />}
                        label={
                          <Box>
                            <Typography>Custom Voice / Video Rooms</Typography>
                            <Typography variant="caption" color="text.secondary">+$10/month</Typography>
                          </Box>
                        }
                      />
                    </Box> */}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => handlePriceSelected(
                        getPrices('prod_tl_individual-2025-wGCk4x2O6p3j'),
                        1
                      )}
                    >
                      Start{shouldTrial && ' 30 day Free Trial'}
                    </Button>
                  </Box>
                </Box>
              </StyledCardContent>
            </PricingCard>
          </Grid>
        )}

        {/* Business Plan */}
        {enabledProducts.includes('prod_tl_business-2025-N9kH4gDqXydb') && (
          <Grid item xs={12} md={4}>
            <PricingCard>
              <StyledCardContent>
                <Box>
                  <Typography variant="h5" component="div" fontWeight={600} gutterBottom>
                    Business Plan
                  </Typography>
                  <Typography variant="h3" component="div" gutterBottom>
                    <Typography component="span">AUD</Typography>${productOptions['prod_tl_business-2025-N9kH4gDqXydb'].prices?.[billingInterval].amount ?? '?'}
                    <Typography component="span" variant="subtitle1">/user/{billingInterval}</Typography>
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" textAlign="center" gutterBottom>
                    COLLECT & MEET
                  </Typography>
                  <List>
                    <ListItem>Everything in "Individual Plan" +</ListItem>
                    {productOptions['prod_tl_business-2025-N9kH4gDqXydb'].description.map((feature, index) => (
                      <ListItem key={index} alignItems="flex-start" disablePadding>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <CheckCircleIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ fontSize: "15px" }} sx={{ marginTop: "8px" }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Box>
                  {/* <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Box>
                          <Typography>Custom Voice / Video Rooms</Typography>
                          <Typography variant="caption" color="text.secondary">+$10/month</Typography>
                        </Box>
                      }
                    />
                  </Box> */}
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => handlePriceSelected(
                      getPrices('prod_tl_business-2025-N9kH4gDqXydb')
                    )}
                  >
                    Start{shouldTrial && ' 30 Day Free Trial'}
                  </Button>
                </Box>
              </StyledCardContent>
            </PricingCard>
          </Grid>
        )}

        {/* Enterprise Plan */}
        {enabledProducts.includes('enterprise') && (
          <Grid item xs={4}>
            <PricingCard>
              <StyledCardContent>
                <Box>
                  <Typography variant="h5" component="div" textAlign="center" gutterBottom>
                    Enterprise Plan
                  </Typography>
                  <Typography variant="h3" component="div" textAlign="center" gutterBottom>
                    $
                    <Typography component="span" variant="h3"> Contact</Typography>
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" textAlign="center" gutterBottom>
                    TBA
                  </Typography>
                  <List>
                    {productOptions.enterprise.description.map((feature, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <CheckCircleIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ fontSize: "15px" }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  href="/signup"
                >
                  Contact Sales
                </Button>
              </StyledCardContent>
            </PricingCard>
          </Grid>
        )}
      </Grid>
      <UserQuantityDialog
        onConfirm={handleQuantitySelected}
        onCancel={() => {setPriceSelected(null)}}
        defaultQuantity={defaultQuantity}
        unitPrice={unitPrice}
        interval={billingInterval}
        visible={priceSelected !== null}
      />
    </Section>
  )
}

export default PricingSection;
